import cx from 'classnames';
import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getTitilerBaseUrl, getApiBaseUrl } from 'utilities/environment';

const sqlApi = 'https://cdb-cdn.resilienceatlas.org/user/ra/api/v2/sql';

export const useWidget = ({ slug, geojson }, { type, analysisQuery, analysisBody }) => {
  const isCOGLayer = useMemo(() => type === 'cog', [type]);
  const query = useMemo(() => {
    if (!analysisQuery) return null;

    if (analysisBody) {
      const { assetId, params: providedParams } = JSON.parse(analysisBody);
      let parsedQuery = analysisQuery;

      // For COG layers, auto-inject titilerUrl if not provided in params
      // This allows layer configs to omit titilerUrl and use environment-based defaults
      const params = isCOGLayer
        ? {
            titilerUrl: getTitilerBaseUrl(),
            apiUrl: getApiBaseUrl(),
            ...providedParams, // Allow explicit params to override defaults
          }
        : providedParams;

      if (isCOGLayer && params && typeof params === 'object') {
        Object.entries(params).forEach(([key, value]) => {
          parsedQuery = parsedQuery.replace(`{{${key}}}`, value);
        });
      }

      return {
        method: 'post',
        url: parsedQuery,
        data: {
          assetId,
          ...(isCOGLayer
            ? { ...L.geoJSON(geojson).toGeoJSON() }
            : { geometry: L.geoJSON(geojson).toGeoJSON() }),
        },
      };
    }

    const geometry = geojson.features ? geojson.features[0].geometry : geojson.geometry || geojson;

    const q = analysisQuery.replace(/{{geometry}}/g, JSON.stringify(geometry));

    return {
      method: 'get',
      url: sqlApi,
      params: {
        q,
      },
    };
  }, [analysisBody, geojson, analysisQuery, isCOGLayer]);

  const {
    data,
    isFetching: loading,
    isSuccess,
    isError,
  } = useQuery({
    queryKey: ['widget', query],
    queryFn: ({ signal }) => axios({ ...query, signal }).then((res) => res.data),
    enabled: !!query,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
  const loaded = isSuccess || isError;

  const rootWidgetProps = useCallback(
    () => ({
      id: `widget-${slug.replace(' ', '-')}`,
      className: cx('m-widget', {
        'is-loading': loading,
        loaded,
      }),
    }),
    [loading, loaded, slug],
  );

  const noData =
    !data ||
    (isCOGLayer ? !data.features || !data.features.length : !data.rows || !data.rows.length);

  let parsedData = data;
  if (isCOGLayer) {
    const statistics = data?.features?.[0]?.properties?.statistics;
    const firstColumn = statistics && Object.values(statistics)?.[0];
    const { histogram } = firstColumn || {};
    const rows = histogram?.[0].map((count, i) => ({
      mappingValue: histogram[1][i],
      count,
    }));
    parsedData = { rows, stats: firstColumn };
  }
  return {
    rootWidgetProps,
    loaded,
    loading,
    data: parsedData,
    noData,
  };
};
